import {
  getBattlefieldUnitAtZoneIndex,
  isUnitStillOnBattlefield,
} from '../utils/battleLethalDeathFx';
import decommissionAnimationDetector from './decommissionAnimationDetector';

const { isDecommissionedUnitInHand, DECOMMISSION_DEPLOY_PROVIDER_IDS } = decommissionAnimationDetector;

const SKIP_DEPLOY_DAMAGE_VARIANTS = new Set([
  'frontal_harassment_blue_cross',
  'encrazed_bombardier_fire_arc',
]);

function resolveOpponentId(gameState, viewerUserId) {
  const players = gameState?.players || Object.keys(gameState?.state || {});
  return players.find((id) => String(id) !== String(viewerUserId)) ?? null;
}

/**
 * Compare pre/post deploy game state and attach HP metadata for deploy projectile damage FX.
 */
export function enrichDeployAnimationDamageMeta(anim, prevState, nextState, viewerUserId) {
  if (!anim?.toSlot || anim.toSlot.zone === 'general' || anim.toSlot.index == null) {
    return anim;
  }
  if (anim.deployAnimationVariant && SKIP_DEPLOY_DAMAGE_VARIANTS.has(anim.deployAnimationVariant)) {
    return anim;
  }
  if (!nextState?.state) {
    return anim;
  }

  const opponentId = resolveOpponentId(nextState, viewerUserId);
  const { zone, index } = anim.toSlot;

  const liveUnit = getBattlefieldUnitAtZoneIndex(nextState, viewerUserId, opponentId, zone, index);
  const prevUnit = prevState
    ? getBattlefieldUnitAtZoneIndex(prevState, viewerUserId, opponentId, zone, index)
    : null;

  if (prevUnit && !liveUnit && typeof prevUnit.health === 'number' && prevUnit.health > 0) {
    const targetPlayerId = anim.toSlot?.playerId
      ?? (anim.toSlot?.zone?.startsWith('your_') ? viewerUserId : opponentId);
    const targetHand = targetPlayerId ? nextState.state?.[targetPlayerId]?.hand : null;
    const deployDecommissionsTarget =
      anim.deployUnitId != null
      && DECOMMISSION_DEPLOY_PROVIDER_IDS.includes(Number(anim.deployUnitId));
    if (deployDecommissionsTarget || isDecommissionedUnitInHand(targetHand, prevUnit)) {
      return anim;
    }
    if (targetPlayerId && isUnitStillOnBattlefield(nextState, targetPlayerId, prevUnit.instanceId)) {
      return anim;
    }
    return {
      ...anim,
      targetInstanceId: prevUnit.instanceId,
      healthBefore: prevUnit.health,
      healthAfter: 0,
      damageAmount: prevUnit.health,
      ghostUnit: { ...prevUnit },
    };
  }

  if (!liveUnit || !prevUnit || prevUnit.instanceId !== liveUnit.instanceId) {
    return anim;
  }

  const healthBefore = prevUnit.health;
  const healthAfter = liveUnit.health;
  if (typeof healthBefore !== 'number' || typeof healthAfter !== 'number' || healthAfter >= healthBefore) {
    return anim;
  }

  return {
    ...anim,
    targetInstanceId: liveUnit.instanceId,
    healthBefore,
    healthAfter,
    damageAmount: healthBefore - healthAfter,
    ghostUnit: { ...prevUnit },
  };
}

/** Hold pre-hit HP during deploy projectile flight (survivor or lethal). */
export function shouldHoldHealthForDeployTargetAnimation(anim) {
  return Boolean(
    anim?.targetInstanceId
    && typeof anim.healthBefore === 'number'
    && typeof anim.healthAfter === 'number'
    && anim.healthBefore > anim.healthAfter
  );
}
